<template>
  <header class="sticky top-0 z-50 flex items-center justify-between whitespace-nowrap border-b border-solid border-primary/10 px-6 md:px-20 py-4 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md shadow-sm">
    <router-link
      to="/"
      class="flex items-center min-h-[44px] min-w-[44px] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <img
        src="/logos/QRestoo-logo.png"
        alt="QRestoo"
        width="112"
        height="32"
        class="h-8 w-28 sm:h-11 sm:w-44 rounded-xl object-contain"
        data-testid="landing-header-logo"
      />
    </router-link>

    <!-- Desktop nav -->
    <nav class="hidden md:flex flex-1 justify-center gap-8" aria-label="Main">
      <a
        href="#features"
        class="inline-flex items-center min-h-[44px] py-2 text-slate-600 dark:text-slate-400 text-sm font-semibold hover:text-primary transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        @click.prevent="scrollToSection('features')"
      >{{ $t('landing.nav.features') }}</a>
      <a
        href="#faq"
        class="inline-flex items-center min-h-[44px] py-2 text-slate-600 dark:text-slate-400 text-sm font-semibold hover:text-primary transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        @click.prevent="scrollToSection('faq')"
      >{{ $t('landing.nav.solutions') }}</a>
      <a
        href="#pricing"
        class="inline-flex items-center min-h-[44px] py-2 text-slate-600 dark:text-slate-400 text-sm font-semibold hover:text-primary transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        @click.prevent="scrollToSection('pricing')"
      >{{ $t('landing.nav.pricing') }}</a>
    </nav>

    <div class="flex items-center gap-3">
      <label for="landing-locale-select" class="sr-only">{{ $t('app.languageLabel') }}</label>
      <select
        id="landing-locale-select"
        :value="appLocale"
        class="min-h-[44px] rounded-xl border border-primary/20 bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100 px-4 py-2 text-sm font-semibold focus:ring-2 focus:ring-primary focus:outline-none cursor-pointer"
        :aria-label="$t('app.ariaAppLanguage')"
        data-testid="landing-locale-select"
        @change="onLocaleChange($event.target.value)"
      >
        <option v-for="loc in APP_LOCALES" :key="loc.code" :value="loc.code">
          {{ loc.flag }} {{ loc.label }}
        </option>
      </select>

      <!-- Desktop: Go to app -->
      <router-link
        :to="{ name: 'Login' }"
        class="hidden md:flex min-w-[100px] min-h-[44px] cursor-pointer items-center justify-center rounded-xl px-4 border border-primary/20 text-primary text-sm font-bold hover:bg-primary/5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {{ $t('landing.nav.goToApp') }}
      </router-link>

      <!-- Mobile: hamburger -->
      <button
        type="button"
        class="md:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        :aria-expanded="mobileMenuOpen"
        aria-controls="landing-mobile-nav"
        aria-label="Open menu"
        data-testid="landing-mobile-menu-button"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </header>

  <!-- Mobile nav overlay + panel -->
  <Teleport to="body">
    <Transition name="mobile-menu">
      <div
        v-show="mobileMenuOpen"
        class="fixed inset-0 z-[100] md:hidden"
        aria-hidden="true"
      >
        <button
          type="button"
          class="absolute inset-0 bg-black/50"
          aria-label="Close menu"
          @click="mobileMenuOpen = false"
        />
        <nav
          id="landing-mobile-nav"
          class="landing-mobile-nav-panel absolute top-0 right-0 bottom-0 w-full max-w-[280px] bg-background-light dark:bg-background-dark border-l border-primary/10 shadow-xl flex flex-col py-6 px-4"
          aria-label="Main"
        >
          <div class="flex flex-col gap-1">
            <a
              href="#features"
              class="flex items-center min-h-[48px] px-4 py-3 rounded-xl text-slate-700 dark:text-slate-300 font-semibold hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              @click="onNavClick('#features')"
            >
              {{ $t('landing.nav.features') }}
            </a>
            <a
              href="#faq"
              class="flex items-center min-h-[48px] px-4 py-3 rounded-xl text-slate-700 dark:text-slate-300 font-semibold hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              @click="onNavClick('#faq')"
            >
              {{ $t('landing.nav.solutions') }}
            </a>
            <a
              href="#pricing"
              class="flex items-center min-h-[48px] px-4 py-3 rounded-xl text-slate-700 dark:text-slate-300 font-semibold hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              @click="onNavClick('#pricing')"
            >
              {{ $t('landing.nav.pricing') }}
            </a>
          </div>
          <div class="mt-6 pt-4 border-t border-primary/10">
            <router-link
              :to="{ name: 'Login' }"
              class="flex items-center justify-center min-h-[48px] rounded-xl px-4 border border-primary/20 text-primary font-bold hover:bg-primary/5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              @click="onMobileLoginClick"
            >
              {{ $t('landing.nav.goToApp') }}
            </router-link>
          </div>
        </nav>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { APP_LOCALES, setStoredAppLocale } from '@/config/app-locales'
const { locale } = useI18n()
const mobileMenuOpen = ref(false)

function scrollToSection(id) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  window.history.replaceState(null, '', `#${id}`)
}

function onMobileLoginClick() {
  mobileMenuOpen.value = false
}

function onNavClick(hash) {
  mobileMenuOpen.value = false
  const id = hash.replace('#', '')
  scrollToSection(id)
}

const appLocale = computed({
  get: () => locale.value,
  set: (v) => { locale.value = v },
})

function applyDocDir(code) {
  document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr'
}

function onLocaleChange(code) {
  if (!APP_LOCALES.some((l) => l.code === code)) return
  appLocale.value = code
  setStoredAppLocale(code)
  applyDocDir(code)
}

// Close mobile menu when viewport grows to md
function closeIfDesktop() {
  if (window.matchMedia('(min-width: 768px)').matches) {
    mobileMenuOpen.value = false
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('resize', closeIfDesktop)
  onUnmounted(() => {
    window.removeEventListener('resize', closeIfDesktop)
    document.body.style.overflow = ''
  })
}

watch(mobileMenuOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<style scoped>
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.2s ease;
}
.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}
.mobile-menu-enter-from .landing-mobile-nav-panel,
.mobile-menu-leave-to .landing-mobile-nav-panel {
  transform: translateX(100%);
}
.mobile-menu-enter-active .landing-mobile-nav-panel,
.mobile-menu-leave-active .landing-mobile-nav-panel {
  transition: transform 0.2s ease;
}
</style>
